/**
 * Estado global del módulo radio.
 *
 * Diseño:
 *  - Una sola instancia del estado se exporta como `state`.
 *  - Las mutaciones se hacen vía funciones (`setVolume`, `setPlaying`...)
 *    para que sea fácil añadir validación, eventos o logging en el futuro.
 *  - Los lookups derivados (playlist activa, track actual, etc.) son
 *    funciones puras que reciben el estado.
 *
 * NO contiene UI ni efectos secundarios sobre el DOM. Esos viven en
 * los módulos de UI (Fase 4b).
 */

import { DEFAULT_HOTSPOTS, DEFAULT_PLAYLIST } from './data.js';
import type { HotspotMap, Playlist, RadioState, ScrubMode, Track } from './types.js';

/** Crea el estado inicial. Útil para tests y para reset completo. */
export function createInitialState(): RadioState {
  return {
    isOpen: false,
    activePlaylistId: DEFAULT_PLAYLIST.id,
    currentTrackId: null,
    isPlaying: false,
    isPaused: false,
    scrubMode: null,
    volume: 0.7,
    knobAngle: 0,
    userTracks: [],
    serverTracks: [],
    playlists: [],
    editMode: false,
    hotspots: { ...DEFAULT_HOTSPOTS },
  };
}

/** Estado vivo del módulo. Único en toda la app. */
export const state: RadioState = createInitialState();

// ─── Mutadores tipados ───────────────────────────────────────────────────
// Punto único de mutación. Si alguna acción debe disparar otros efectos
// (re-render, persistencia), se centraliza aquí.

export function setOpen(isOpen: boolean): void {
  state.isOpen = isOpen;
}

export function setActivePlaylist(id: string): void {
  state.activePlaylistId = id;
}

export function setCurrentTrack(id: string | null): void {
  state.currentTrackId = id;
}

export function setPlaying(isPlaying: boolean): void {
  state.isPlaying = isPlaying;
}

export function setPaused(isPaused: boolean): void {
  state.isPaused = isPaused;
}

export function setScrubMode(mode: ScrubMode): void {
  state.scrubMode = mode;
}

export function setVolume(v: number): void {
  state.volume = Math.max(0, Math.min(1, v));
}

export function setKnobAngle(angle: number): void {
  state.knobAngle = angle;
}

export function setEditMode(on: boolean): void {
  state.editMode = on;
}

export function setHotspots(hotspots: HotspotMap): void {
  state.hotspots = hotspots;
}

export function setPlaylists(playlists: Playlist[]): void {
  state.playlists = playlists;
}

export function setUserTracks(tracks: Track[]): void {
  state.userTracks = tracks;
}

export function setServerTracks(tracks: Track[]): void {
  state.serverTracks = tracks;
}

// ─── Lookups derivados (funciones puras) ─────────────────────────────────

/** Devuelve la playlist activa o `undefined` si el id no existe. */
export function getActivePlaylist(s: RadioState): Playlist | undefined {
  return s.playlists.find((p) => p.id === s.activePlaylistId);
}

/** Busca una playlist por ID. */
export function getPlaylist(s: RadioState, id: string): Playlist | undefined {
  return s.playlists.find((p) => p.id === id);
}

/**
 * Devuelve TODAS las tracks accesibles ahora mismo: las del servidor
 * (playlist del escuadrón) seguidas de las del usuario.
 */
export function getAllTracks(s: RadioState): Track[] {
  return [...s.serverTracks, ...s.userTracks];
}

/** Busca una track por ID en cualquier fuente. */
export function getTrack(s: RadioState, id: string): Track | undefined {
  return getAllTracks(s).find((t) => t.id === id);
}

/** Devuelve la track actual (null si no hay ninguna seleccionada). */
export function getCurrentTrack(s: RadioState): Track | null {
  if (!s.currentTrackId) return null;
  return getTrack(s, s.currentTrackId) ?? null;
}
